import React, { useEffect, useRef, useState } from 'react';
import FocusTrap from 'focus-trap-react';
import type { MutableRefObject } from 'react';
import { SwishPanelMaskStyled, SwishPanelWrapperStyled } from './SwishPanel.styled';
import { usePortal } from '../../hooks/usePortal';
import { SWISHPANEL_CONTENT, SWISHPANEL_MASK } from '../../util/data-id';
import type { SwishPanelProps } from './types';

interface Props extends SwishPanelProps {
  onClose(): void;
  alwaysOnTop?: boolean;
  disableMaskClick?: boolean;
  children: React.ReactNode
}

const SwishPanel = ({
  open,
  onClose,
  toggle,
  alwaysOnTop = false,
  disableMaskClick = false,
  children,
}: Props): JSX.Element => {
  const [ active, setActive ] = useState(false);
  const scrollAmount = useRef<number | null>(null);
  const mask = React.useRef() as MutableRefObject<HTMLDivElement>;

  useEffect(() => {
    // DOM html
    const html = document.getElementsByTagName('html')[0];
    // DOM body
    const body = document.body;

    // useRef
    const { current } = mask;

    // TRANSITIONS TO BE TRIGGERED
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName === 'transform') {
        setActive(open);

        if (!open) {
          html.removeAttribute('class');
          body.removeAttribute('class');
          window.scroll(0, scrollAmount.current ?? 0);
          scrollAmount.current = null;
          onClose();
        }
      }
    };

    // CLOSE WHEN ESC KEY IS PRESSED
    const onEscKey = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        toggle(false);
      }
    };

    // CLOSE WHEN CLICK ON MASK
    const onClickOnMask = (event: MouseEvent) => {
      if (!disableMaskClick && event.target === current) {
        toggle(false);
      }
    };

    // NEEDED IN ORDER TO TRIGGER ENDING TRANSITION
    if (open) {
      if (scrollAmount.current === null) {
        scrollAmount.current = window.scrollY;
      }
      html.classList.add('overflow-hidden');
      body.classList.add('overflow-hidden');

      setTimeout(() => {
        setActive(open);
      }, 10);
    }

    // ADDS EVENT LISTENERS
    if (current) {
      current.addEventListener('transitionend', onTransitionEnd);
      current.addEventListener('click', onClickOnMask);
      window.addEventListener('keyup', onEscKey);
    }

    // REMOVES EVENT LISTENERS
    return () => {
      if (current) {
        current.removeEventListener('transitionend', onTransitionEnd);
        current.removeEventListener('click', onClickOnMask);
      }
      window.removeEventListener('keyup', onEscKey);
    };
  }, [ open, toggle ]);

  const portal = usePortal(
    <FocusTrap
      active={open || active}
      focusTrapOptions={{
        initialFocus: `[data-id="${SWISHPANEL_MASK}"]`,
        fallbackFocus: `[data-id="${SWISHPANEL_MASK}"]`,
      }}
    >
      <SwishPanelMaskStyled
        ref={mask}
        tabIndex={0}
        data-id={SWISHPANEL_MASK}
        data-toggle={active && open && 'true'}
        $alwaysOnTop={alwaysOnTop}
      >
        <SwishPanelWrapperStyled data-id={SWISHPANEL_CONTENT}>
          <div>{children}</div>
        </SwishPanelWrapperStyled>
      </SwishPanelMaskStyled>
    </FocusTrap>,
    'portal-swishpanel',
  );

  return (open || active) ? portal : <></>;
};

SwishPanel.displayName = 'SwishPanel';

export { SwishPanel as default };
